import assert from "assert";
import type { BigNumber } from "bignumber.js";
import {
  ColorType,
  createChart,
  CrosshairMode,
  DeepPartial,
  PriceFormat,
  TrackingModeExitMode,
} from "lightweight-charts";

import { CHART_BACK_GREEN, CHART_BACK_RED } from "@/constants/common";
import type { Candle } from "@/types/common";

import { SelectedCandle } from "./types";

const dynamicHeight = () => {
  assert(typeof window !== "undefined", "clientside only");
  let callback: ((h: number) => void) | undefined;
  const getHeight = () =>
    window.innerWidth >= 724 ? 416 : window.innerHeight - 392;

  const handler = () => callback?.(getHeight());
  window.addEventListener("resize", handler);

  return {
    initialHeight: getHeight(),
    setCallback(cb: NonNullable<typeof callback>) {
      callback = cb;
    },
    unsub: () => window.removeEventListener("resize", handler),
  };
};

export const createChartBase = ({
  container,
  width: _width,
}: {
  container?: string | HTMLElement;
  width: number;
}) => {
  assert(typeof window !== "undefined", "clientside only");
  assert(container, "empty container");
  let width = _width;
  const dyn = dynamicHeight();

  const chart = createChart(container, {
    autoSize: true,
    width,
    height: dyn.initialHeight,
    crosshair: {
      mode: CrosshairMode.Normal,
      // TODO: looks like alpha does not work on canvas
      // horzLine: {
      //   labelBackgroundColor: "rgb(255,255,255,0.1)",
      // },
    },
    layout: {
      background: { type: ColorType.Solid, color: "transparent" },
    },
    rightPriceScale: {
      borderVisible: false,
      autoScale: true,
    },
    timeScale: {
      borderVisible: false,
    },
    grid: {
      vertLines: {
        visible: false,
      },
      horzLines: {
        visible: false,
      },
    },
    trackingMode: { exitMode: TrackingModeExitMode.OnTouchEnd },
  });

  dyn.setCallback((h) => chart.resize(width, h));

  chart
    .timeScale()
    .applyOptions({ visible: false, fixLeftEdge: true, fixRightEdge: true });

  const candleStickSeries = chart.addCandlestickSeries({
    upColor: "#40BF00",
    downColor: "#E64595",
    borderVisible: false,
    wickUpColor: "#26a69a",
    wickDownColor: "#ef5350",
    priceScaleId: "right",
    priceLineVisible: false,
  });

  const lineSeries = chart.addLineSeries({
    color: "#40BF00",
    priceScaleId: "right",
    lineWidth: 2,
    priceLineVisible: false,
  });

  const volumeColor = getComputedStyle(
    document.documentElement,
  ).getPropertyValue("--fill-secondary");

  const volumeSeries = chart.addHistogramSeries({
    color: volumeColor ?? "rgba(204, 187, 184, 0.16)",
    base: -2, // add little height to be more beautiful
    priceFormat: {
      type: "volume",
    },
    priceScaleId: "",
  });

  volumeSeries.priceScale().applyOptions({
    scaleMargins: {
      top: 0.6,
      bottom: 0,
    },
  });

  return {
    candleStickSeries,
    chart,
    lineSeries,
    volumeSeries,

    unsub() {
      dyn.unsub();
      chart.removeSeries(candleStickSeries);
      chart.removeSeries(lineSeries);
      chart.remove();
    },
  };
};

export const getSelectedCandle = (
  actualCandle: Candle,
  prevCandle: Candle,
): SelectedCandle => {
  const { close: actualClose } = actualCandle;
  const { close: prevClose } = prevCandle;
  const diff = actualClose.minus(prevClose);
  const diffPercent = diff.div(prevClose).times(100).toNumber();

  return {
    ...actualCandle,
    diff,
    diffPercent,
    color: diff.lte(0) ? CHART_BACK_RED : CHART_BACK_GREEN,
  };
};

export const getPriceFormat = (p: BigNumber): DeepPartial<PriceFormat> => {
  if (p.gt(10)) {
    return {
      type: "price",
      precision: 2,
      minMove: 0.01,
    };
  }

  let decimals = 4;
  let leadingZeros = 0;
  const s = p.toString().split(".");

  if (s[1]) {
    const match = s[1].match(/^0+/);
    match && (leadingZeros = match[0].length);
  }

  const precision = decimals + leadingZeros;

  return {
    type: "price",
    precision,
    minMove: 1 / Math.pow(10, precision),
  };
};
